import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Users, Zap, TrendingUp, RefreshCw, BarChart3, AlertTriangle,
  CalendarIcon, Loader2, FileText, Send, TrendingDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, subDays, subMonths, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { toast } from 'sonner';

// Import refactored components
import {
  SectionHeader,
  HealthGrade,
  ScoreCard,
  TopPerformingPosts,
  NotificationStatus,
  BestPostingTime,
  PostDetailModal,
  GrowthAnalysis,
  EngagementMetrics,
  ContentDistribution,
  RecentPostsTable,
  SignalMetrics,
  ViewsBySource,
  DATE_RANGES
} from './channel-analytic';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChannelAnalyticContent({ ib }) {
  const [channelType, setChannelType] = useState('public');
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [mtprotoStats, setMtprotoStats] = useState(null);
  const [mtprotoStatus, setMtprotoStatus] = useState(null);
  const [mtprotoPosts, setMtprotoPosts] = useState(null);
  const [signalMetrics, setSignalMetrics] = useState(null);
  const [signalMetricsLoading, setSignalMetricsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [postsPage, setPostsPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [channelConfigs, setChannelConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(true);
  const postsPerPage = 8;
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('mtd');
  
  const hasChannelConfigs = channelConfigs.length > 0;
  const getToken = () => localStorage.getItem('token');
  
  // Fetch channel configurations for this IB
  useEffect(() => {
    const fetchChannelConfigs = async () => {
      if (!ib?.id) return;
      setConfigsLoading(true);
      try {
        const token = getToken();
        const response = await axios.get(`${API}/api/telegram/channels/config/${ib.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChannelConfigs(response.data.configs || []);
      } catch (err) {
        console.error('Error fetching channel configs:', err);
        setChannelConfigs([]);
      } finally {
        setConfigsLoading(false);
      }
    };
    fetchChannelConfigs();
  }, [ib?.id]);
  
  // Get current channel config
  const currentChannelConfig = useMemo(() => {
    return channelConfigs.find(c => c.channel_type === channelType) || null;
  }, [channelType, channelConfigs]);
  
  // Available channel types for selector
  const availableChannels = useMemo(() => {
    return channelConfigs.map(c => ({
      type: c.channel_type,
      name: c.name || `${ib?.name || 'Channel'} (${c.channel_type})`,
      username: c.username
    }));
  }, [channelConfigs, ib?.name]);
  
  // Calculate date range boundaries
  const getDateRangeBounds = useCallback((range) => {
    const now = new Date();
    switch (range) {
      case '7d': return { start: subDays(now, 7), end: now };
      case '30d': return { start: subDays(now, 30), end: now };
      case 'this_month': return { start: startOfMonth(now), end: now };
      case 'last_month': 
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case '90d': return { start: subDays(now, 90), end: now };
      case 'today': 
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { start: todayStart, end: now };
      case 'yesterday':
        const yesterday = subDays(now, 1);
        const yesterdayStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        const yesterdayEnd = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);
        return { start: yesterdayStart, end: yesterdayEnd };
      case 'jan_2026':
        return { start: new Date(2026, 0, 1), end: new Date(2026, 0, 31, 23, 59, 59) };
      case 'feb_2026':
        return { start: new Date(2026, 1, 1), end: new Date(2026, 1, 28, 23, 59, 59) };
      case 'mar_2026':
        return { start: new Date(2026, 2, 1), end: endOfMonth(new Date(2026, 2, 1)) };
      case 'all':
        return { start: new Date(2025, 0, 1), end: now };
      default: return { start: subDays(now, 30), end: now };
    }
  }, []);
  
  // Fetch comprehensive analytics
  const fetchAnalytics = useCallback(async (type, forceRefresh = false) => {
    if (!hasChannelConfigs) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      // Use unified endpoint that works for all IBs
      const url = `${API}/api/telegram/analytics/${ib.id}/${type}${forceRefresh ? '?force_refresh=true' : ''}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAnalytics(response.data);
      } else {
        setError(response.data.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [hasChannelConfigs, ib?.id]);
  
  // Fetch MTProto stats (full statistics like Telegram app) - ALL channels
  const fetchMtprotoStats = useCallback(async (type, forceRefresh = false) => {
    if (!hasChannelConfigs || !ib?.id) return;
    
    try {
      const token = getToken();
      const forceParam = forceRefresh ? '?force_refresh=true' : '';
      const [statusRes, statsRes, postsRes] = await Promise.all([
        axios.get(`${API}/api/mtproto/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/mtproto/channel-stats/${ib.id}/${type}${forceParam}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/api/mtproto/recent-posts/${ib.id}/${type}?limit=50${forceRefresh ? '&force_refresh=true' : ''}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      
      setMtprotoStatus(statusRes.data);
      if (statsRes.data?.success) setMtprotoStats(statsRes.data);
      if (postsRes.data?.success) setMtprotoPosts(postsRes.data);
      setPostsPage(1);
    } catch (err) {
      console.error('Error fetching MTProto stats:', err);
    }
  }, [hasChannelConfigs, ib?.id]);
  
  // Fetch Signal Metrics from Daily Data Operation
  const fetchSignalMetrics = useCallback(async (type, dateBounds) => {
    if (!ib?.id) return;
    
    setSignalMetricsLoading(true);
    try {
      const token = getToken();
      const startDate = dateBounds.start.toISOString().split('T')[0];
      const endDate = dateBounds.end.toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API}/api/signal-metrics/${ib.id}/${type}?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        setSignalMetrics(response.data.metrics);
      } else {
        setSignalMetrics(null);
      }
    } catch (err) {
      console.error('Error fetching signal metrics:', err);
      setSignalMetrics(null);
    } finally {
      setSignalMetricsLoading(false);
    }
  }, [ib?.id]);
  
  // Seed growth data (for initial setup)
  const seedGrowthData = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${API}/api/telegram/seed-growth/${channelType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Growth data seeded!', { description: response.data.message });
        fetchAnalytics(channelType, true);
      }
    } catch (err) {
      toast.error('Error seeding growth data');
    }
  };
  
  // Refresh - force refresh to bypass cache
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = getToken();
      // First try bot API refresh to get latest subscriber counts
      try {
        await axios.post(`${API}/api/bot/refresh-all-channels`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {}
      const bounds = getDateRangeBounds(dateRange);
      await fetchAnalytics(channelType, true);
      await fetchMtprotoStats(channelType, true);
      await fetchSignalMetrics(channelType, bounds);
      toast.success('Data refreshed!');
    } catch {
      toast.error('Refresh failed');
    }
    setRefreshing(false);
  };
  
  // Reset state when IB or channel changes
  useEffect(() => {
    setAnalytics(null);
    setMtprotoStats(null);
    setMtprotoPosts(null);
    setSignalMetrics(null);
    setPostsPage(1);
    setChannelType('public'); // Reset to public when IB changes
  }, [ib?.id]);
  
  // Reset state when channel type changes within same IB
  useEffect(() => {
    setAnalytics(null);
    setMtprotoStats(null);
    setMtprotoPosts(null);
    setSignalMetrics(null);
    setPostsPage(1);
  }, [channelType, ib?.id]);
  
  // Reset pagination when date range changes
  useEffect(() => {
    setPostsPage(1);
  }, [dateRange]);
  
  // Initial fetch - triggered when IB or channel changes
  useEffect(() => {
    if (hasChannelConfigs && !configsLoading && ib?.id) {
      fetchAnalytics(channelType);
      fetchMtprotoStats(channelType);
    }
  }, [hasChannelConfigs, configsLoading, ib?.id, channelType, fetchAnalytics, fetchMtprotoStats]);
  
  // Fetch signal metrics when channel or date range changes
  useEffect(() => {
    if (hasChannelConfigs && !configsLoading && ib?.id) {
      const bounds = getDateRangeBounds(dateRange);
      fetchSignalMetrics(channelType, bounds);
    }
  }, [hasChannelConfigs, configsLoading, ib?.id, channelType, dateRange, fetchSignalMetrics, getDateRangeBounds]);
  
  // Extract data from analytics response
  const channel = analytics?.channel || {};
  const rawGrowthMetrics = analytics?.growth?.metrics || {};
  // Transform API metrics to match component expected format
  const growthMetrics = {
    ...rawGrowthMetrics,
    net_change: rawGrowthMetrics.monthly || 0,
    growth_rate: rawGrowthMetrics.rate || 0
  };
  const growthHistory = analytics?.growth?.history || [];
  const recentPosts = mtprotoPosts?.posts || analytics?.posts?.recent || [];
  
  // MTProto data (full stats from Telegram) - for all channels with MTProto access
  const mtStats = mtprotoStats || {};
  
  // Get posts data from API response
  const postsData = analytics?.posts || {};
  const postsStats = postsData.stats || {};
  
  // Calculate real metrics - use posts data for all IBs
  const viewsPerPost = mtStats.views_per_post?.current || postsStats.avg_views || 0;
  
  // Calculate ERR (Engagement Rate) from available data
  // ERR = (avg_views / subscribers) * 100
  const subscriberCount = mtStats.followers?.current || channel.subscribers || 0;
  const calculatedERR = subscriberCount > 0 ? ((viewsPerPost / subscriberCount) * 100).toFixed(2) : 0;
  
  // Parse followers graph for joined/left data from MTProto (all channels)
  let followersJoined = 0;
  let followersLeft = 0;
  
  if (mtStats.followers_graph?.columns) {
    const cols = mtStats.followers_graph.columns;
    if (cols[1] && Array.isArray(cols[1])) {
      const joinedData = cols[1].filter(v => typeof v === 'number');
      followersJoined = joinedData.slice(-7).reduce((a, b) => a + Math.abs(b), 0);
    }
    if (cols[2] && Array.isArray(cols[2])) {
      const leftData = cols[2].filter(v => typeof v === 'number');
      followersLeft = leftData.slice(-7).reduce((a, b) => a + Math.abs(b), 0);
    }
  } else if (growthHistory.length >= 2) {
    // Calculate from growth history for non-FIRA IBs
    const recentHistory = growthHistory.slice(-7);
    let totalGrowth = 0;
    
    for (let i = 1; i < recentHistory.length; i++) {
      const change = recentHistory[i].subscribers - recentHistory[i-1].subscribers;
      if (change > 0) followersJoined += change;
      else followersLeft += Math.abs(change);
      totalGrowth += change;
    }
    
    // Always ensure we have both joined and left estimates
    // Typical churn rate is 5-15% of growth
    if (followersJoined > 0 && followersLeft === 0) {
      // Estimate churn based on typical patterns
      followersLeft = Math.round(followersJoined * 0.12); // ~12% churn estimate
    }
    if (followersLeft > 0 && followersJoined === 0) {
      followersJoined = Math.round(followersLeft * 0.08);
    }
    
    // If still no data, estimate from net growth
    if (followersJoined === 0 && followersLeft === 0 && totalGrowth !== 0) {
      if (totalGrowth > 0) {
        followersJoined = Math.round(totalGrowth * 1.15); // Gross joins
        followersLeft = Math.round(totalGrowth * 0.15);    // Estimated leaves
      } else {
        followersLeft = Math.abs(totalGrowth);
        followersJoined = Math.round(Math.abs(totalGrowth) * 0.1);
      }
    }
  } else if (subscriberCount > 0) {
    // Fallback estimate based on subscriber count and growth rate
    const monthlyGrowth = growthMetrics.monthly || 0;
    if (monthlyGrowth > 0) {
      followersJoined = Math.round(monthlyGrowth * 1.12 / 4); // Weekly estimate
      followersLeft = Math.round(monthlyGrowth * 0.12 / 4);
    } else {
      // Minimal estimate for very low activity channels
      followersJoined = Math.round(subscriberCount * 0.005);
      followersLeft = Math.round(subscriberCount * 0.003);
    }
  }
  
  // Filter growth history by date range
  const dateBounds = getDateRangeBounds(dateRange);
  const filteredGrowthHistory = useMemo(() => {
    if (!growthHistory.length) return [];
    return growthHistory.filter(h => {
      const date = new Date(h.date);
      return isWithinInterval(date, { start: dateBounds.start, end: dateBounds.end });
    });
  }, [growthHistory, dateBounds]);
  
  // Parse MTProto followers graph for REAL joined/left data per day
  const mtprotoGrowthData = useMemo(() => {
    if (!mtStats.followers_graph?.columns || mtStats.followers_graph.columns.length < 3) {
      return null;
    }
    
    const cols = mtStats.followers_graph.columns;
    const timestamps = cols[0].slice(1);
    const joinedData = cols[1].slice(1);
    const leftData = cols[2].slice(1);
    
    return timestamps.map((ts, idx) => {
      const date = new Date(ts);
      return {
        date: format(date, 'dd/MM'),
        fullDate: date.toISOString(),
        subsIn: Math.abs(joinedData[idx] || 0),
        subsOut: Math.abs(leftData[idx] || 0),
        netChange: (joinedData[idx] || 0) - Math.abs(leftData[idx] || 0)
      };
    });
  }, [mtStats.followers_graph]);
  
  // Growth chart data - merge MTProto real data with interpolated snapshots
  const growthChartData = useMemo(() => {
    // Build a map of real MTProto joined/left data by date
    const mtprotoMap = {};
    if (mtprotoGrowthData && mtprotoGrowthData.length > 0) {
      mtprotoGrowthData.forEach(d => {
        const dateKey = d.fullDate?.split('T')[0];
        if (dateKey) {
          mtprotoMap[dateKey] = { subsIn: d.subsIn, subsOut: d.subsOut };
        }
      });
    }
    
    if (!filteredGrowthHistory.length) return [];
    
    return filteredGrowthHistory.map((h, idx, arr) => {
      const prevSubs = idx > 0 ? arr[idx - 1].subscribers : h.subscribers;
      const netChange = h.subscribers - prevSubs;
      const dateKey = h.date?.split('T')[0];
      
      // Use real MTProto data if available for this date
      const mtData = mtprotoMap[dateKey];
      
      let subsIn, subsOut, isEstimated;
      if (mtData) {
        subsIn = mtData.subsIn;
        subsOut = mtData.subsOut;
        isEstimated = false;
      } else {
        // Estimate from net change
        const dailyChurnEstimate = Math.max(1, Math.round(h.subscribers * 0.002));
        if (netChange >= 0) {
          subsOut = dailyChurnEstimate;
          subsIn = netChange + subsOut;
        } else {
          subsIn = Math.max(0, dailyChurnEstimate - Math.abs(netChange));
          subsOut = subsIn + Math.abs(netChange);
        }
        isEstimated = true;
      }
      
      return {
        date: format(new Date(h.date), 'dd/MM'),
        fullDate: h.date,
        subscribers: h.subscribers,
        subsIn: Math.max(0, subsIn),
        subsOut: Math.max(0, subsOut),
        netChange: netChange,
        isEstimated
      };
    });
  }, [mtprotoGrowthData, filteredGrowthHistory, dateBounds, subscriberCount]);
  
  // Filter posts by date range for Content Distribution
  const filteredPosts = useMemo(() => {
    if (!recentPosts.length) return [];
    return recentPosts.filter(p => {
      if (!p.date) return true;
      const postDate = new Date(p.date);
      return isWithinInterval(postDate, { start: dateBounds.start, end: dateBounds.end });
    });
  }, [recentPosts, dateBounds]);
  
  // Calculate engagement rates from posts - now filtered by date range
  const engagementMetrics = useMemo(() => {
    const posts = filteredPosts || [];
    if (!posts.length) return null;
    
    let totalReactions = 0;
    let postsWithReactions = 0;
    let postsWithPolls = posts.filter(p => p.has_poll).length;
    
    posts.forEach(p => {
      const reactionCount = p.reactions ? Object.values(p.reactions).reduce((a, b) => typeof b === 'number' ? a + b : a, 0) : 0;
      if (reactionCount > 0) postsWithReactions++;
      totalReactions += reactionCount;
    });
    
    const avgReactionsPerPost = posts.length > 0 ? totalReactions / posts.length : 0;
    const reactionRate = subscriberCount > 0 ? (avgReactionsPerPost / subscriberCount * 100) : 0;
    
    // Use REAL poll stats from API if available
    const apiPollStats = analytics?.poll_stats?.stats;
    let pollRate;
    let actualPolls;
    
    if (apiPollStats && apiPollStats.total_polls > 0) {
      // Use real poll rate from tracked data
      // Poll Rate = (Total Votes / Total Poll Views) * 100
      pollRate = apiPollStats.poll_rate || 0;
      actualPolls = apiPollStats.total_polls;
      postsWithPolls = actualPolls;
    } else if (postsWithPolls > 0) {
      // Fallback: estimate from posts that have polls
      pollRate = (postsWithPolls / posts.length * 100);
      actualPolls = postsWithPolls;
    } else {
      // Channel has posts but no polls - show 0% (not "Pending")
      pollRate = 0;
      actualPolls = 0;
      postsWithPolls = 0;
    }
    
    return {
      totalReactions,
      avgReactionsPerPost: Math.round(avgReactionsPerPost),
      reactionRate: reactionRate.toFixed(2),
      pollRate: pollRate !== null ? pollRate.toFixed(1) : null,
      postsWithReactions,
      postsWithPolls: actualPolls,
      // Additional poll info from API
      totalVotes: apiPollStats?.total_votes || 0,
      totalPollViews: apiPollStats?.total_poll_views || 0,
      avgVotesPerPoll: apiPollStats?.avg_votes_per_poll || 0,
      hasPollData: apiPollStats && apiPollStats.total_polls > 0
    };
  }, [filteredPosts, subscriberCount, analytics?.poll_stats]);
  
  // Paginated posts
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const paginatedPosts = useMemo(() => {
    const start = (postsPage - 1) * postsPerPage;
    return filteredPosts.slice(start, start + postsPerPage);
  }, [filteredPosts, postsPage, postsPerPage]);
  
  // Content distribution filtered by date
  const filteredContentStats = useMemo(() => {
    const posts = filteredPosts;
    return {
      total: posts.length,
      photo: posts.filter(p => p.has_photo).length,
      video: posts.filter(p => p.has_video).length,
      text: posts.filter(p => !p.has_photo && !p.has_video).length
    };
  }, [filteredPosts]);
  
  // Calculate Health Score (A-D) — calibrated for typical Telegram channel benchmarks
  // Industry: ERR 5-15% is healthy, growth 0.5%/wk is solid, reactions 0.1%+ is good
  const healthScore = useMemo(() => {
    const breakdown = [];

    // ERR (Engagement Rate Reach) — 30 pts
    const err = viewsPerPost > 0 && subscriberCount > 0 ? (viewsPerPost / subscriberCount * 100) : 0;
    let errPts = 0;
    if (err >= 10) errPts = 30;
    else if (err >= 5) errPts = 25;
    else if (err >= 3) errPts = 20;
    else if (err >= 1) errPts = 15;
    else if (err > 0) errPts = 10;
    breakdown.push({ label: `ERR ${err.toFixed(1)}%`, score: errPts, max: 30, pct: errPts / 30 });

    // Growth rate — 25 pts (over the selected period)
    const netGrowth = followersJoined - followersLeft;
    const growthRate = subscriberCount > 0 ? (netGrowth / subscriberCount * 100) : 0;
    let growthPts = 0;
    if (growthRate >= 2) growthPts = 25;
    else if (growthRate >= 0.5) growthPts = 20;
    else if (growthRate >= 0) growthPts = 15;
    else if (growthRate >= -1) growthPts = 10;
    else growthPts = 5;
    breakdown.push({ label: `Growth ${growthRate.toFixed(2)}%`, score: growthPts, max: 25, pct: growthPts / 25 });

    // Reaction rate — 25 pts
    const reactRate = parseFloat(engagementMetrics?.reactionRate || 0);
    let reactPts = 0;
    if (reactRate >= 0.3) reactPts = 25;
    else if (reactRate >= 0.1) reactPts = 20;
    else if (reactRate >= 0.05) reactPts = 15;
    else if (reactRate > 0) reactPts = 10;
    else if (subscriberCount > 0) reactPts = 5;
    breakdown.push({ label: `React Rate ${reactRate.toFixed(2)}%`, score: reactPts, max: 25, pct: reactPts / 25 });

    // Posting frequency — 20 pts (compute posts/week based on actual date window)
    const windowDays = Math.max(1, Math.ceil((dateBounds.end - dateBounds.start) / (1000 * 60 * 60 * 24)));
    const postsPerWeek = ((recentPosts?.length || 0) / windowDays) * 7;
    let postPts = 0;
    if (postsPerWeek >= 7) postPts = 20;
    else if (postsPerWeek >= 3) postPts = 15;
    else if (postsPerWeek >= 1) postPts = 10;
    else if (postsPerWeek > 0) postPts = 7;
    else postPts = 3;
    breakdown.push({ label: `Posts/Week ${postsPerWeek.toFixed(1)}`, score: postPts, max: 20, pct: postPts / 20 });

    const score = errPts + growthPts + reactPts + postPts;
    const maxScore = 100;
    const percentage = Math.round((score / maxScore) * 100);
    let grade = 'D';
    if (percentage >= 80) grade = 'A';
    else if (percentage >= 60) grade = 'B';
    else if (percentage >= 40) grade = 'C';

    return { grade, score: percentage, breakdown };
  }, [viewsPerPost, subscriberCount, followersJoined, followersLeft, engagementMetrics, recentPosts, dateBounds]);
  
  // Top Performing Posts (by views and engagement)
  const topPerformingPosts = useMemo(() => {
    if (!filteredPosts.length) return [];
    
    return [...filteredPosts]
      .map(post => {
        const reactionCount = post.reactions 
          ? Object.values(post.reactions).reduce((a, b) => typeof b === 'number' ? a + b : a, 0) 
          : 0;
        const reach = subscriberCount > 0 ? (post.views / subscriberCount * 100) : 0;
        const err = post.views > 0 ? (reactionCount / post.views * 100) : 0;
        
        return {
          ...post,
          reactionCount,
          reach,
          err,
          performanceScore: (post.views / 1000) * 0.6 + (err * 10) * 0.4
        };
      })
      .sort((a, b) => b.performanceScore - a.performanceScore)
      .slice(0, 5);
  }, [filteredPosts, subscriberCount]);
  
  // Notification Rate
  const notificationStats = useMemo(() => {
    // For FIRA: use MTProto data
    // For others: estimate based on typical Telegram channel patterns
    let enabled, total, rate;
    
    if (mtStats.enabled_notifications?.part) {
      // Use real MTProto data (FIRA)
      enabled = mtStats.enabled_notifications.part;
      total = mtStats.enabled_notifications.total || subscriberCount || 1;
    } else {
      // Estimate for other IBs based on typical patterns
      total = subscriberCount || 1;
      const estimatedRate = channel.channel_type === 'vip' ? 0.55 : 0.35;
      const engagementBoost = viewsPerPost > 0 && subscriberCount > 0 
        ? Math.min((viewsPerPost / subscriberCount) * 2, 0.15) 
        : 0;
      const finalRate = Math.min(estimatedRate + engagementBoost, 0.70);
      enabled = Math.round(total * finalRate);
    }
    
    rate = total > 0 ? (enabled / total * 100) : 0;
    const disabled = total - enabled;
    const isEstimated = !mtStats.enabled_notifications?.part;
    
    return {
      enabled: Math.round(enabled),
      disabled: Math.round(disabled),
      total: Math.round(total),
      rate: rate.toFixed(1),
      isGood: rate >= 50,
      isMedium: rate >= 30 && rate < 50,
      isEstimated
    };
  }, [mtStats.enabled_notifications, subscriberCount, channel.channel_type, viewsPerPost]);
  
  // Best Posting Time Analysis
  const bestPostingTime = useMemo(() => {
    if (!recentPosts.length) return null;
    
    const hourStats = {};
    
    recentPosts.forEach(post => {
      if (!post.date) return;
      const hour = new Date(post.date).getHours();
      
      if (!hourStats[hour]) {
        hourStats[hour] = { posts: 0, totalViews: 0, totalReactions: 0 };
      }
      
      const reactionCount = post.reactions 
        ? Object.values(post.reactions).reduce((a, b) => typeof b === 'number' ? a + b : a, 0) 
        : 0;
      
      hourStats[hour].posts++;
      hourStats[hour].totalViews += post.views || 0;
      hourStats[hour].totalReactions += reactionCount;
    });
    
    const hourData = Object.entries(hourStats).map(([hour, stats]) => ({
      hour: parseInt(hour),
      label: `${hour.padStart(2, '0')}:00`,
      posts: stats.posts,
      avgViews: Math.round(stats.totalViews / stats.posts),
      avgReactions: Math.round(stats.totalReactions / stats.posts),
      score: (stats.totalViews / stats.posts) + (stats.totalReactions / stats.posts * 50)
    })).sort((a, b) => a.hour - b.hour);
    
    const sortedByScore = [...hourData].sort((a, b) => b.score - a.score);
    const bestHours = sortedByScore.slice(0, 3);
    const worstHours = sortedByScore.slice(-2);
    
    const timeSlots = [
      { name: 'Morning', range: '06:00-12:00', hours: [6,7,8,9,10,11], color: '#F59E0B' },
      { name: 'Afternoon', range: '12:00-18:00', hours: [12,13,14,15,16,17], color: '#3B82F6' },
      { name: 'Evening', range: '18:00-24:00', hours: [18,19,20,21,22,23], color: '#8B5CF6' },
      { name: 'Night', range: '00:00-06:00', hours: [0,1,2,3,4,5], color: '#1F2937' }
    ].map(slot => {
      const slotPosts = hourData.filter(h => slot.hours.includes(h.hour));
      const totalPosts = slotPosts.reduce((a, b) => a + b.posts, 0);
      const avgViews = slotPosts.length > 0 
        ? Math.round(slotPosts.reduce((a, b) => a + b.avgViews * b.posts, 0) / (totalPosts || 1))
        : 0;
      return { ...slot, posts: totalPosts, avgViews };
    });
    
    return {
      hourData,
      bestHours,
      worstHours,
      timeSlots,
      recommendation: bestHours[0] 
        ? `Best time to post: ${bestHours[0].label} (avg ${bestHours[0].avgViews.toLocaleString()} views)`
        : null
    };
  }, [recentPosts]);
  
  // Get date range label
  const dateRangeLabel = DATE_RANGES.find(r => r.value === dateRange)?.label || 'Last 30 Days';
  
  // Loading state for channel configs
  if (configsLoading) {
    return (
      <div className="flex flex-col h-full">
        <header className="flex-shrink-0 px-8 py-6 border-b border-[hsl(var(--border))] bg-[#0A0A0B]">
          <h1 className="text-2xl font-bold text-white">Channel Analytic</h1>
          <p className="text-sm text-[#9CA3AF]">Telegram Channel Analytics</p>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-[#E63946]" size={48} />
        </div>
      </div>
    );
  }
  
  // No channels configured - show message
  if (!hasChannelConfigs) {
    return (
      <div className="flex flex-col h-full">
        <header className="flex-shrink-0 px-8 py-6 border-b border-[hsl(var(--border))] bg-[#0A0A0B]">
          <h1 className="text-2xl font-bold text-white">Channel Analytic</h1>
          <p className="text-sm text-[#9CA3AF]">Telegram Channel Analytics</p>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <AlertTriangle size={48} className="mx-auto mb-4 text-[#F59E0B]" />
            <h3 className="text-xl font-bold text-white mb-2">No Telegram Channels Configured</h3>
            <p className="text-[#9CA3AF]">This IB does not have any Telegram channels linked yet.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full" data-testid="channel-analytic-page">
      {/* Header */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Channel Analytic</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">
              {currentChannelConfig?.name || channel.title || 'Telegram Channel'} • {dateRangeLabel}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Date Range Selector */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-28 sm:w-40 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs sm:text-sm">
                <CalendarIcon size={12} className="mr-1 sm:mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                {DATE_RANGES.map(range => (
                  <SelectItem key={range.value} value={range.value} className="text-white">
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Channel Selector */}
            <Select value={channelType} onValueChange={setChannelType}>
              <SelectTrigger className="w-32 sm:w-56 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                {availableChannels.map(ch => (
                  <SelectItem key={ch.type} value={ch.type} className="text-white">
                    <div className="flex items-center gap-2">
                      {ch.type === 'vip' ? (
                        <Zap size={14} className="text-[#F59E0B]" />
                      ) : (
                        <Users size={14} className="text-[#3B82F6]" />
                      )}
                      {ch.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportOpen(true)}
              className="bg-[#E63946]/10 border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/20"
              data-testid="channel-report-btn"
            >
              <FileText size={14} className="mr-1.5" />
              Channel Report
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="border-[hsl(var(--border))] text-[hsl(var(--text-muted))]"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="overflow-x-hidden">
        <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6 w-full">

          {/* MTProto session warning — visible when session is dead so data won't sync */}
          {mtprotoStatus && mtprotoStatus.authenticated === false && (
            <div className="rounded-xl border border-[#F59E0B]/40 bg-gradient-to-r from-[#F59E0B]/10 to-transparent p-4 flex items-start gap-3" data-testid="mtproto-warning-banner">
              <AlertTriangle size={20} className="text-[#F59E0B] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold text-[#F59E0B] mb-0.5">MTProto session expired — channel data tak akan sync</div>
                <p className="text-xs text-[hsl(var(--text-muted))] leading-relaxed">
                  Sections macam Posts, Engagement, Content Distribution, Views by Source bergantung pada MTProto. Sila buka{' '}
                  <span className="text-white font-semibold">Admin Panel → TG Reports / Scorecard tab</span> untuk re-authenticate.
                </p>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-12 text-center">
              <Loader2 size={48} className="mx-auto mb-4 animate-spin text-[#E63946]" />
              <p className="text-white">Fetching data from Telegram...</p>
            </div>
          )}
          
          {/* Error */}
          {!loading && error && (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-12 text-center">
              <AlertTriangle size={48} className="mx-auto mb-4 text-[#E63946]" />
              <h3 className="text-xl font-bold text-white mb-2">Error</h3>
              <p className="text-[#9CA3AF] mb-4">{error}</p>
              <Button onClick={handleRefresh} className="bg-[#E63946]">
                <RefreshCw size={14} className="mr-2" />
                Try Again
              </Button>
            </div>
          )}
          
          {/* Main Content */}
          {!loading && !error && analytics?.success && (
            <>
              {/* Row 1: Health Score + Channel Score */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <HealthGrade grade={healthScore.grade} score={healthScore.score} breakdown={healthScore.breakdown} />
                
                <div className="col-span-3 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <SectionHeader icon={BarChart3} title="Channel Performance" subtitle={`${dateRangeLabel}`} />
                  
                  <div className="p-5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      <ScoreCard 
                        label="SUBSCRIBERS" 
                        value={subscriberCount?.toLocaleString() || '0'}
                        change={mtStats.followers?.previous ? ((subscriberCount - mtStats.followers.previous) / mtStats.followers.previous * 100).toFixed(1) : growthMetrics.growth_rate || 0}
                        isUp={mtStats.followers?.previous ? (subscriberCount > mtStats.followers.previous) : growthMetrics.net_change > 0}
                        available={true}
                      />
                      <ScoreCard 
                        label="AVG VIEWS" 
                        value={viewsPerPost > 0 ? Math.round(viewsPerPost).toLocaleString() : "N/A"}
                        change={mtStats.views_per_post?.previous ? ((viewsPerPost - mtStats.views_per_post.previous) / mtStats.views_per_post.previous * 100).toFixed(1) : null}
                        isUp={mtStats.views_per_post?.previous ? viewsPerPost > mtStats.views_per_post.previous : null}
                        available={viewsPerPost > 0}
                      />
                      <ScoreCard 
                        label="ERR %" 
                        value={viewsPerPost > 0 && subscriberCount ? `${((viewsPerPost / subscriberCount) * 100).toFixed(1)}%` : "N/A"}
                        change={null}
                        isUp={null}
                        available={viewsPerPost > 0}
                      />
                      <ScoreCard 
                        label="JOINED" 
                        value={followersJoined > 0 ? `+${followersJoined.toLocaleString()}` : (growthMetrics.net_change > 0 ? `+${growthMetrics.net_change}` : "0")}
                        change={null}
                        isUp={true}
                        color="#10B981"
                        available={followersJoined > 0 || growthMetrics.net_change > 0}
                        subtitle="Last 7 days"
                      />
                      <ScoreCard 
                        label="LEFT" 
                        value={followersLeft > 0 ? `-${followersLeft.toLocaleString()}` : "0"}
                        change={null}
                        isUp={false}
                        color="#E63946"
                        available={followersLeft > 0}
                        subtitle="Last 7 days"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section 2: Growth Analysis */}
              <GrowthAnalysis 
                growthChartData={growthChartData}
                growthMetrics={growthMetrics}
                dateRangeLabel={dateRangeLabel}
                onSeedGrowthData={seedGrowthData}
              />
              
              {/* Section 2.5: Signal Performance Metrics */}
              <SignalMetrics 
                metrics={signalMetrics}
                channelType={channelType}
                dateRangeLabel={dateRangeLabel}
                isLoading={signalMetricsLoading}
              />
              
              {/* Section 3 & 4: Engagement Metrics + Content Distribution */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <EngagementMetrics 
                  engagementMetrics={engagementMetrics}
                  filteredPosts={filteredPosts}
                  viewsPerPost={viewsPerPost}
                  subscriberCount={subscriberCount}
                  dateRangeLabel={dateRangeLabel}
                />
                <ContentDistribution 
                  filteredContentStats={filteredContentStats}
                  dateRangeLabel={dateRangeLabel}
                />
              </div>
              
              {/* Section 4.5: Views by Source - New! */}
              <ViewsBySource 
                channelStats={mtprotoStats}
                postsData={filteredPosts}
              />
              
              {/* Section 5: Top Performing Posts & Notification Rate */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <TopPerformingPosts 
                  topPerformingPosts={topPerformingPosts}
                  dateRangeLabel={dateRangeLabel}
                  channelType={channelType}
                />
                <NotificationStatus notificationStats={notificationStats} />
              </div>
              
              {/* Section 6: Best Posting Time Analysis */}
              <BestPostingTime bestPostingTime={bestPostingTime} />
              
              {/* Section 7: Recent Posts Performance */}
              <RecentPostsTable 
                filteredPosts={filteredPosts}
                paginatedPosts={paginatedPosts}
                postsPage={postsPage}
                setPostsPage={setPostsPage}
                totalPages={totalPages}
                postsPerPage={postsPerPage}
                subscriberCount={subscriberCount}
                dateRangeLabel={dateRangeLabel}
                onPostClick={setSelectedPost}
              />
            </>
          )}
        </div>
      </div>
      
      {/* Post Detail Modal */}
      <PostDetailModal 
        selectedPost={selectedPost}
        onClose={() => setSelectedPost(null)}
        channelType={channelType}
      />

      {/* ==================== CHANNEL REPORT MODAL ==================== */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-3xl bg-[#0a0a0f] border-[#1a1a25] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-lg font-bold tracking-tight">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#E63946] to-[#c62828] flex items-center justify-center">
                <FileText size={17} className="text-white" />
              </div>
              Channel Report
            </DialogTitle>
          </DialogHeader>

          {/* Period Selector — Month picker + Week / MTD / 30 Days */}
          {(() => {
            // Build list of months going back 12 months
            const monthOptions = [];
            const today = new Date();
            for (let i = 0; i < 13; i++) {
              const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
              monthOptions.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: format(d, 'MMMM yyyy') });
            }
            const selectedMonth = (reportPeriod.startsWith('m:') ? reportPeriod.slice(2) : null) ||
              (reportPeriod === '30d' ? null : `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
            return (
              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#9ca3af]">Month:</span>
                  <Select value={selectedMonth || 'recent'} onValueChange={v => setReportPeriod(v === 'recent' ? '30d' : `m:${v}`)}>
                    <SelectTrigger className="w-[220px] h-9 text-sm bg-[#111118] border-[#2a2a35]" data-testid="report-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                      <SelectItem value="recent">Last 30 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#9ca3af] mr-2">View:</span>
                  {[
                    { id: 'mtd', label: 'Full Month' },
                    { id: 'w1', label: 'Week 1' },
                    { id: 'w2', label: 'Week 2' },
                    { id: 'w3', label: 'Week 3' },
                    { id: 'w4', label: 'Week 4' },
                  ].map(p => {
                    const active = (reportPeriod === 'mtd' && p.id === 'mtd') ||
                      (reportPeriod === p.id) ||
                      (reportPeriod.startsWith('m:') && p.id === 'mtd');
                    return (
                      <button key={p.id} onClick={() => setReportPeriod(p.id)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${active ? 'bg-[#E63946] text-white shadow-lg shadow-[#E63946]/20' : 'bg-[#111118] text-[#9ca3af] border border-[#2a2a35] hover:text-white hover:border-[#3a3a45]'}`}
                        data-testid={`report-view-${p.id}`}
                      >{p.label}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {(() => {
            // Resolve target month (defaults to current)
            const today = new Date();
            let year = today.getFullYear();
            let month = today.getMonth();
            let useRangeOnly = false;
            if (reportPeriod.startsWith('m:')) {
              const [y, m] = reportPeriod.slice(2).split('-').map(Number);
              year = y; month = m - 1;
            } else if (reportPeriod === '30d') {
              useRangeOnly = true;
            }
            const monthStart = new Date(year, month, 1);
            const monthEnd = new Date(year, month + 1, 0, 23, 59, 59);
            const lastDayOfMonth = monthEnd.getDate();

            let periodStart, periodEnd, periodLabel;
            if (useRangeOnly) {
              periodEnd = new Date();
              periodStart = subDays(periodEnd, 30);
              periodLabel = 'Last 30 Days';
            } else if (reportPeriod === 'mtd' || reportPeriod.startsWith('m:')) {
              periodStart = monthStart;
              periodEnd = monthEnd > today ? today : monthEnd;
              periodLabel = format(monthStart, 'MMMM yyyy');
            } else if (reportPeriod.startsWith('w')) {
              const wn = parseInt(reportPeriod[1], 10);
              const startDay = (wn - 1) * 7 + 1;
              const endDay = Math.min(wn * 7, lastDayOfMonth);
              periodStart = new Date(year, month, startDay);
              periodEnd = new Date(year, month, endDay, 23, 59, 59);
              periodLabel = `Week ${wn} (${format(periodStart, 'dd/MM')} - ${format(periodEnd, 'dd/MM')}) — ${format(monthStart, 'MMMM yyyy')}`;
            } else {
              periodStart = monthStart;
              periodEnd = monthEnd > today ? today : monthEnd;
              periodLabel = format(monthStart, 'MMMM yyyy');
            }

            // Filter posts to selected period (from full recentPosts list)
            const reportPosts = (recentPosts || []).filter(p => {
              if (!p.date) return true;
              const pd = new Date(p.date);
              return pd >= periodStart && pd <= periodEnd;
            });

            const chName = currentChannelConfig?.name || `${ib?.name || ''} (${channelType})`;
            const subs = subscriberCount || 0;
            const views = reportPosts.length > 0
              ? Math.round(reportPosts.reduce((s, p) => s + (p.views || 0), 0) / reportPosts.length)
              : (viewsPerPost || 0);
            const err = subs > 0 && views > 0 ? ((views / subs) * 100).toFixed(1) : '0';
            // Estimate joined/left by filtering growth history for the period
            const periodHistory = (growthHistory || []).filter(h => {
              const hd = new Date(h.date);
              return hd >= periodStart && hd <= periodEnd;
            });
            let pJoined = 0, pLeft = 0;
            for (let i = 1; i < periodHistory.length; i++) {
              const diff = periodHistory[i].subscribers - periodHistory[i - 1].subscribers;
              if (diff > 0) pJoined += diff; else pLeft += Math.abs(diff);
            }
            // If period yields nothing, fall back to overall computed numbers
            const joined = pJoined || followersJoined || 0;
            const left = pLeft || followersLeft || 0;
            const netGrowth = joined - left;
            const growthRate = subs > 0 ? ((netGrowth / subs) * 100).toFixed(2) : '0';
            const totalPosts = reportPosts.length;
            const periodDays = Math.max(1, Math.round((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1);
            const postsPerDay = totalPosts > 0 ? (totalPosts / periodDays).toFixed(1) : '0';
            const photoCount = reportPosts.filter(p => p.has_photo).length;
            const videoCount = reportPosts.filter(p => p.has_video).length;
            const textCount = reportPosts.filter(p => !p.has_photo && !p.has_video && (p.text || p.caption)).length;
            const bestPost = reportPosts.length > 0 ? reportPosts.reduce((best, p) => (p.views || 0) > (best.views || 0) ? p : best, reportPosts[0]) : null;
            const avgReactions = reportPosts.length > 0
              ? (reportPosts.reduce((s, p) => s + Object.values(p.reactions || {}).reduce((a, b) => a + b, 0), 0) / reportPosts.length).toFixed(1)
              : '0';
            const notifRate = notificationStats?.percentage || 0;
            const cScore = analytics?.content_score || null;

            // Recommendations
            const recs = [];
            if (parseFloat(err) < 3 && subs > 0) recs.push({type:'warn', text:`ERR rendah (${err}%) — cuba tukar posting time atau tambah visual content`});
            if (netGrowth < 0) recs.push({type:'critical', text:`Subscriber menurun (${netGrowth}) — audit content quality, kurangkan spam posts`});
            if (parseFloat(postsPerDay) < 0.5 && totalPosts > 0) recs.push({type:'warn', text:`Posting frequency rendah (${postsPerDay}/day) — minimum 1 post/hari`});
            if (parseFloat(postsPerDay) > 5) recs.push({type:'info', text:`High frequency (${postsPerDay}/day) — pastikan quality maintained`});
            if (notifRate > 0 && notifRate < 30) recs.push({type:'warn', text:`Notification rate rendah (${notifRate}%) — create engaging pinned content`});
            if (photoCount === 0 && videoCount === 0 && totalPosts > 3) recs.push({type:'warn', text:'Tiada visual content — tambah photo/video untuk boost engagement'});
            if (netGrowth > 0 && parseFloat(err) > 5) recs.push({type:'good', text:`Channel performing well — growth +${netGrowth}, ERR ${err}%`});
            if (recs.length === 0 && subs > 0) recs.push({type:'good', text:'Channel dalam keadaan stabil. Maintain consistency.'});

            return (
              <div id="channel-report-content" className="space-y-6" style={{background:'#0a0a0f'}}>
                {/* ──── Report Header ──── */}
                <div className="relative p-5 rounded-2xl overflow-hidden border border-[#E63946]/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#E63946]/15 via-[#0a0a0f] to-[#3b82f6]/10" />
                  <div className="relative flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#E63946] mb-1.5">Channel Analytics Report</div>
                      <h3 className="text-xl font-bold text-white tracking-tight">{chName}</h3>
                      <p className="text-xs text-[#9ca3af] mt-1">{periodLabel}</p>
                    </div>
                    {/* Radial Health Gauge */}
                    <div className="relative w-24 h-24">
                      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                        <circle cx="40" cy="40" r="34" fill="none" stroke="#1a1a25" strokeWidth="6" />
                        <circle cx="40" cy="40" r="34" fill="none"
                          stroke={parseFloat(err) >= 5 ? '#10b981' : parseFloat(err) >= 2 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="6" strokeLinecap="round"
                          strokeDasharray={`${Math.min(parseFloat(err) * 10, 213.6)} 213.6`} />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-base font-bold font-mono text-white">{err}%</span>
                        <span className="text-[9px] text-[#9ca3af] uppercase tracking-wider">ERR</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ──── Key Metrics ──── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9ca3af]">Key Metrics</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {[
                      { label: 'Subscribers', val: subs.toLocaleString(), color: '#3b82f6' },
                      { label: 'Avg Views', val: Math.round(views).toLocaleString(), color: '#10b981' },
                      { label: 'ERR', val: `${err}%`, color: parseFloat(err) >= 5 ? '#10b981' : '#f59e0b' },
                      { label: 'Joined', val: `+${joined}`, color: '#10b981' },
                      { label: 'Left', val: `-${left}`, color: '#ef4444' },
                      { label: 'Net', val: netGrowth >= 0 ? `+${netGrowth}` : `${netGrowth}`, color: netGrowth >= 0 ? '#10b981' : '#ef4444' },
                    ].map(m => (
                      <div key={m.label} className="p-3 rounded-xl bg-gradient-to-b from-[#111118] to-[#0d0d12] border border-[#1a1a25] text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{background: `linear-gradient(90deg, transparent, ${m.color}, transparent)`}} />
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9ca3af] mb-1.5">{m.label}</div>
                        <div className="text-base font-bold font-mono" style={{color: m.color}}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ──── Growth & Engagement ──── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9ca3af]">Growth & Engagement</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-4 rounded-xl bg-gradient-to-b from-[#111118] to-[#0d0d12] border border-[#1a1a25] relative overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{background: `linear-gradient(90deg, transparent, ${parseFloat(growthRate) >= 0 ? '#10b981' : '#ef4444'}, transparent)`}} />
                      <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#9ca3af] mb-2">Subscriber Growth</div>
                      <div className="text-2xl font-bold font-mono" style={{color: parseFloat(growthRate) >= 0 ? '#10b981' : '#ef4444'}}>
                        {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-[11px]">
                        <span className="text-[#10b981]">▲ {joined} joined</span>
                        <span className="text-[#ef4444]">▼ {left} left</span>
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-gradient-to-b from-[#111118] to-[#0d0d12] border border-[#1a1a25] relative overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{background: 'linear-gradient(90deg, transparent, #3b82f6, transparent)'}} />
                      <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#9ca3af] mb-2">Engagement Rate</div>
                      <div className="text-2xl font-bold font-mono text-[#3b82f6]">{err}%</div>
                      <div className="text-[11px] text-[#9ca3af] mt-2">
                        {Math.round(views)} avg views · {avgReactions} reactions/post
                      </div>
                      {notifRate > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-[10px] mb-1">
                            <span className="text-[#9ca3af]">Notification Rate</span>
                            <span className="font-mono font-bold" style={{color: notifRate >= 40 ? '#10b981' : '#f59e0b'}}>{notifRate.toFixed(1)}%</span>
                          </div>
                          <div className="h-1.5 bg-[#1a1a25] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{width:`${Math.min(notifRate,100)}%`, background: `linear-gradient(90deg, ${notifRate >= 40 ? '#10b981' : '#f59e0b'}, ${notifRate >= 40 ? '#059669' : '#d97706'})`}} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ──── Content Performance ──── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9ca3af]">Content Performance</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      {[
                        { label: 'Total Posts', val: totalPosts, color: '#fff' },
                        { label: 'Posts/Day', val: postsPerDay, color: '#3b82f6' },
                        { label: 'Avg Reactions', val: avgReactions, color: '#f59e0b' },
                        ...(cScore ? [{ label: 'Content Score', val: `${cScore}/10`, color: cScore >= 7 ? '#10b981' : '#f59e0b' }] : []),
                      ].map(m => (
                        <div key={m.label} className="flex justify-between items-center p-2.5 rounded-lg bg-[#111118] border border-[#1a1a25]">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">{m.label}</span>
                          <span className="text-base font-bold font-mono" style={{color: m.color}}>{m.val}</span>
                        </div>
                      ))}
                    </div>
                    <div className="sm:col-span-2 p-4 rounded-xl bg-gradient-to-b from-[#111118] to-[#0d0d12] border border-[#1a1a25] flex items-center gap-4">
                      <div className="relative w-24 h-24 shrink-0">
                        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                          {(() => {
                            const total = photoCount + videoCount + textCount || 1;
                            const segments = [
                              { count: photoCount, color: '#3b82f6' },
                              { count: videoCount, color: '#8b5cf6' },
                              { count: textCount, color: '#4b5563' },
                            ];
                            let offset = 0;
                            return segments.map((s, i) => {
                              const pct = (s.count / total) * 283;
                              const el = <circle key={i} cx="50" cy="50" r="45" fill="none" stroke={s.color} strokeWidth="10" strokeDasharray={`${pct} ${283 - pct}`} strokeDashoffset={-offset} />;
                              offset += pct;
                              return el;
                            });
                          })()}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-xl font-bold font-mono text-white">{totalPosts}</span>
                          <span className="text-[9px] text-[#9ca3af] uppercase tracking-wider">Posts</span>
                        </div>
                      </div>
                      <div className="flex-1 space-y-2.5">
                        {[
                          { label: 'Photo', count: photoCount, color: '#3b82f6' },
                          { label: 'Video', count: videoCount, color: '#8b5cf6' },
                          { label: 'Text Only', count: textCount, color: '#4b5563' },
                        ].map(t => (
                          <div key={t.label}>
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{background: t.color}} />
                                <span className="text-[#cbd5e1]">{t.label}</span>
                              </span>
                              <span className="font-mono text-white">{t.count} <span className="text-[#9ca3af]">({totalPosts > 0 ? ((t.count/totalPosts)*100).toFixed(0) : 0}%)</span></span>
                            </div>
                            <div className="h-1.5 bg-[#1a1a25] rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{width: `${totalPosts > 0 ? (t.count/totalPosts*100) : 0}%`, background: t.color}} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {bestPost && (bestPost.views || 0) > 0 && (
                    <div className="mt-3 p-4 rounded-xl bg-gradient-to-r from-[#10b981]/8 to-[#0a0a0f] border border-[#10b981]/20 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-1 h-full bg-[#10b981]" />
                      <div className="pl-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#10b981] mb-2">Top Performing Post</div>
                        <div className="text-xs text-white/85 leading-relaxed line-clamp-2">{(bestPost.text || bestPost.caption || '').slice(0, 200)}</div>
                        <div className="flex gap-4 mt-2">
                          {[
                            { label: 'Views', val: (bestPost.views || 0).toLocaleString(), color: '#3b82f6' },
                            { label: 'Reactions', val: Object.values(bestPost.reactions || {}).reduce((a,b) => a+b, 0), color: '#f59e0b' },
                            { label: 'Forwards', val: bestPost.forwards || 0, color: '#8b5cf6' },
                          ].map(s => (
                            <div key={s.label} className="text-center">
                              <div className="text-sm font-bold font-mono" style={{color: s.color}}>{s.val}</div>
                              <div className="text-[9px] text-[#9ca3af] uppercase tracking-wider">{s.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ──── Issues & Red Flags ──── */}
                {(() => {
                  const issues = [];
                  if (netGrowth < 0) issues.push({ sev: 'critical', text: `Subscriber loss: ${netGrowth} — channel kehilangan audience`, metric: 'Subscribers' });
                  if (left > joined * 1.5 && left > 10) issues.push({ sev: 'critical', text: `High churn: ${left} left vs ${joined} joined — retention issue`, metric: 'Retention' });
                  if (parseFloat(err) < 2 && subs > 1000) issues.push({ sev: 'warn', text: `ERR sangat rendah (${err}%) — content tak sampai ke audience`, metric: 'Engagement' });
                  if (parseFloat(postsPerDay) < 0.3 && totalPosts > 0) issues.push({ sev: 'warn', text: `Posting hampir terhenti (${postsPerDay}/day) — channel tidak aktif`, metric: 'Activity' });
                  if (totalPosts === 0) issues.push({ sev: 'critical', text: 'Tiada post langsung dalam period ini — channel inactive', metric: 'Activity' });
                  if (views > 0 && parseFloat(avgReactions) < 0.5 && totalPosts > 5) issues.push({ sev: 'warn', text: `Reactions sangat rendah (${avgReactions}/post) — content kurang engaging`, metric: 'Engagement' });
                  if (notifRate > 0 && notifRate < 25) issues.push({ sev: 'warn', text: `Notification rate ${notifRate.toFixed(0)}% — majority audience mute channel`, metric: 'Reach' });
                  if (photoCount === 0 && videoCount === 0 && totalPosts > 5) issues.push({ sev: 'warn', text: 'Semua post text sahaja — tiada visual content', metric: 'Content' });
                  if (views > 0 && subs > 0 && views < subs * 0.01) issues.push({ sev: 'critical', text: `Views < 1% subscribers — possible shadow ban atau dead audience`, metric: 'Reach' });

                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#ef4444]/30 to-transparent" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#ef4444]">Issues & Red Flags</span>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#ef4444]/30 to-transparent" />
                      </div>
                      {issues.length === 0 ? (
                        <div className="p-3 rounded-xl bg-[#10b981]/8 border border-[#10b981]/20 text-[12px] text-[#10b981] flex items-center gap-2">
                          ✓ Tiada isu kritikal dikesan untuk tempoh ini.
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {issues.map((iss, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-xl relative overflow-hidden" style={{background: iss.sev === 'critical' ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)', border: `1px solid ${iss.sev === 'critical' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'}`}}>
                              <div className="absolute top-0 left-0 w-1 h-full" style={{background: iss.sev === 'critical' ? '#ef4444' : '#f59e0b'}} />
                              <div className="pl-2 flex items-center gap-2 flex-1">
                                <AlertTriangle size={13} style={{color: iss.sev === 'critical' ? '#ef4444' : '#f59e0b'}} className="shrink-0" />
                                <span className="text-xs text-white/85 flex-1">{iss.text}</span>
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0" style={{background: iss.sev === 'critical' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)', color: iss.sev === 'critical' ? '#ef4444' : '#f59e0b'}}>{iss.metric}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ──── Action Items ──── */}
                {(() => {
                  const actions = [];
                  if (netGrowth < 0) actions.push({ priority: 'high', text: 'Launch win-back campaign — cross-promote, giveaway, atau collaboration dengan channel lain', deadline: 'This week' });
                  if (parseFloat(postsPerDay) < 0.5 && totalPosts >= 0) actions.push({ priority: 'high', text: 'Schedule minimum 1-2 posts/day — gunakan content calendar dan batch creation', deadline: 'Immediately' });
                  if (parseFloat(err) < 3 && subs > 500) actions.push({ priority: 'medium', text: 'Test different posting times (8AM, 12PM, 8PM) — track which gets highest views', deadline: 'Next 7 days' });
                  if (photoCount === 0 && videoCount === 0 && totalPosts > 0) actions.push({ priority: 'high', text: 'Add visual content — minimum 50% posts should have photo/video/infographic', deadline: 'Next 3 days' });
                  if (parseFloat(avgReactions) < 1 && totalPosts > 3) actions.push({ priority: 'medium', text: 'Boost engagement — add polls, Q&A, atau ask questions dalam post untuk encourage reactions', deadline: 'Next post' });
                  if (notifRate > 0 && notifRate < 30) actions.push({ priority: 'medium', text: 'Pin engaging content — create valuable pinned post supaya new subscribers ON notification', deadline: 'This week' });
                  if (netGrowth > 0 && parseFloat(err) > 3) actions.push({ priority: 'low', text: 'Scale winning formula — increase posting frequency gradually dan maintain content quality', deadline: 'Ongoing' });
                  if (actions.length === 0) actions.push({ priority: 'low', text: 'Channel on track — maintain consistency dan explore new content formats', deadline: 'Ongoing' });

                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#3b82f6]/30 to-transparent" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3b82f6]">Action Items</span>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#3b82f6]/30 to-transparent" />
                      </div>
                      <div className="space-y-1.5">
                        {actions.map((a, i) => {
                          const pColor = a.priority === 'high' ? '#ef4444' : a.priority === 'medium' ? '#f59e0b' : '#10b981';
                          return (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-[#111118] to-[#0d0d12] border border-[#1a1a25] relative overflow-hidden">
                              <div className="absolute top-0 left-0 w-1 h-full" style={{background: pColor}} />
                              <div className="pl-2 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background: `${pColor}20`, color: pColor}}>{a.priority}</span>
                                  <span className="text-[10px] text-[#9ca3af]">{a.deadline}</span>
                                </div>
                                <span className="text-xs text-white/85 leading-relaxed">{a.text}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ──── Recommendations ──── */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9ca3af]">Recommendations</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                  </div>
                  <div className="space-y-2">
                    {recs.map((r, i) => {
                      const colors = { critical: { bg: '#ef4444' }, warn: { bg: '#f59e0b' }, good: { bg: '#10b981' }, info: { bg: '#3b82f6' } };
                      const c = colors[r.type] || colors.info;
                      return (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-[#111118] to-[#0d0d12] border border-[#1a1a25] relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-1 h-full" style={{background: c.bg}} />
                          <div className="pl-2 flex items-center gap-2">
                            {r.type === 'critical' ? <TrendingDown size={14} style={{color: c.bg}} /> :
                             r.type === 'warn' ? <AlertTriangle size={14} style={{color: c.bg}} /> :
                             r.type === 'good' ? <TrendingUp size={14} style={{color: c.bg}} /> :
                             <BarChart3 size={14} style={{color: c.bg}} />}
                            <span className="text-xs text-white/85 leading-relaxed">{r.text}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ──── Export ──── */}
                <div className="flex items-center gap-3 pt-4 border-t border-[#1a1a25]">
                  <button onClick={() => {
                    const el = document.getElementById('channel-report-content');
                    if (el) {
                      import('html2canvas').then(({ default: html2canvas }) => {
                        html2canvas(el, { backgroundColor: '#0a0a0f', scale: 2 }).then(canvas => {
                          const link = document.createElement('a');
                          link.download = `${ib?.name || 'Channel'}_${channelType}_${periodLabel.replace(/\s+/g,'_')}_report.png`;
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                          toast.success('Report exported');
                        });
                      }).catch(() => toast.error('Export failed'));
                    }
                  }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#111118] text-white text-xs font-semibold uppercase tracking-wider border border-[#2a2a35] hover:bg-[#1a1a25] transition-all">
                    <FileText size={14} /> Export Image
                  </button>
                  <button onClick={() => {
                    let text = `CHANNEL ANALYTICS REPORT\n${chName}  |  ${periodLabel}\n${'─'.repeat(40)}\n\n`;
                    text += `SNAPSHOT\nSubscribers: ${subs.toLocaleString()}\nAvg Views: ${Math.round(views).toLocaleString()}\nERR: ${err}%\nGrowth: ${netGrowth >= 0 ? '+' : ''}${netGrowth} (${growthRate}%)\n\n`;
                    text += `CONTENT\nTotal Posts: ${totalPosts} (${postsPerDay}/day)\nMix: ${photoCount} photo, ${videoCount} video, ${textCount} text\nAvg Reactions: ${avgReactions}\n`;
                    if (notifRate > 0) text += `Notification Rate: ${notifRate.toFixed(1)}%\n`;
                    text += `\nRECOMMENDATIONS\n`;
                    recs.forEach(r => { text += `${r.type === 'critical' ? '▼' : r.type === 'warn' ? '▲' : '●'} ${r.text}\n`; });
                    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
                  }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c62828] text-white text-xs font-semibold uppercase tracking-wider hover:shadow-lg hover:shadow-[#E63946]/20 transition-all">
                    <Send size={14} /> Copy & Share
                  </button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
